import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { hideGlobalMessage } from '../store/slices/globalMessageSlice';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../lib/utils';

const GlobalMessage: React.FC = () => {
  const dispatch = useDispatch();
  const globalMessage = useSelector((state: RootState) => state.globalMessage);

  if (!globalMessage.visible) {
    return null;
  }

  // Map alert types to styles and icons
  const typeConfig = {
    success: {
      icon: CheckCircle,
      bg: 'bg-accent-bullish/10',
      border: 'border-accent-bullish/30',
      text: 'text-accent-bullish',
    },
    danger: {
      icon: AlertCircle,
      bg: 'bg-accent-bearish/10',
      border: 'border-accent-bearish/30',
      text: 'text-accent-bearish',
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-accent-warning/10',
      border: 'border-accent-warning/30',
      text: 'text-accent-warning',
    },
    info: {
      icon: Info,
      bg: 'bg-accent-info/10',
      border: 'border-accent-info/30',
      text: 'text-accent-info',
    },
  };

  const config = typeConfig[globalMessage.type as keyof typeof typeConfig] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'fixed top-4 left-1/2 -translate-x-1/2 z-[9999]',
        'animate-slide-down',
        'max-w-xl w-[calc(100%-2rem)]',
        'md:left-[calc(50%+120px)]', // Offset for sidebar on desktop
        'pointer-events-auto',
      )}
      id="globalMessageArea"
    >
      <div
        className={cn(
          'flex items-center justify-between gap-3',
          'px-4 py-3 rounded-lg',
          'border shadow-lg backdrop-blur-sm',
          config.bg,
          config.border,
          'pointer-events-auto',
        )}
        role="alert"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Icon className={cn('h-5 w-5 flex-shrink-0', config.text)} />
          <span className={cn('text-sm font-medium', config.text)} id="globalMessageText">
            {globalMessage.message}
          </span>
        </div>
        <button
          type="button"
          className={cn(
            'p-2 rounded-md',
            'text-text-secondary hover:text-text-primary',
            'hover:bg-surface-container/80',
            'transition-colors',
            'flex-shrink-0',
            'cursor-pointer',
            'z-10',
          )}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(hideGlobalMessage());
          }}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Close"
          title="Click to close message"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default GlobalMessage;
